import React, { useEffect, useMemo, useState } from "react";
import { api, inr, formatApiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell, LineChart, Line
} from "recharts";
import {
  CurrencyInr, TrendUp, TrendDown, Wallet, ArrowsClockwise, FileText, Warning,
  Printer, FilePdf, MicrosoftExcelLogo, WhatsappLogo
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { downloadCSV, printReport } from "@/lib/reportExport";
import { openPrintStatement } from "@/lib/printStatement";

const today = () => new Date().toISOString().slice(0, 10);
const startOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const PIE_COLORS = ["#3b82f6", "#22c55e", "#ef4444", "#f59e0b", "#a855f7", "#06b6d4", "#f97316", "#eab308"];

const PERIODS = [
  { value: "day", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "year", label: "This Year" },
  { value: "all", label: "All Time" },
  { value: "custom", label: "Custom" },
];

const Kpi = ({ label, value, icon: Icon, color, foot }) => (
  <div className="kpi" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="eyebrow flex items-center gap-1.5">
      {Icon ? <Icon size={11} /> : null} {label}
    </div>
    <div className="val" style={color ? { color } : undefined}>{value}</div>
    {foot ? <div className="text-[11px] font-mono mt-1 text-muted-foreground">{foot}</div> : null}
  </div>
);

function ExportBar({ title, onExcel, extraButtons, testidPrefix = "report" }) {
  return (
    <div className="no-print flex items-center justify-between flex-wrap gap-2">
      <div className="text-sm text-muted-foreground">{title}</div>
      <div className="flex items-center gap-2">
        {extraButtons}
        <Button variant="outline" size="sm" className="h-8" onClick={onExcel} data-testid={`${testidPrefix}-excel-btn`}>
          <MicrosoftExcelLogo size={14} className="mr-1.5" /> Excel
        </Button>
        <Button variant="outline" size="sm" className="h-8" onClick={() => printReport("report-print-root")} data-testid={`${testidPrefix}-pdf-btn`}>
          <FilePdf size={14} className="mr-1.5" /> Save PDF
        </Button>
        <Button variant="outline" size="sm" className="h-8" onClick={() => printReport("report-print-root")} data-testid={`${testidPrefix}-print-btn`}>
          <Printer size={14} className="mr-1.5" /> Print
        </Button>
      </div>
    </div>
  );
}

function FilterBar({ period, setPeriod, start, setStart, end, setEnd, onRefresh }) {
  const isCustom = period === "custom";
  return (
    <div className="card-flat p-4 flex flex-wrap items-end gap-3">
      <div className="space-y-1.5 min-w-[160px]">
        <Label className="eyebrow">Period</Label>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger data-testid="report-period"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="eyebrow">From</Label>
        <Input type="date" min="1900-01-01" value={start} onChange={(e) => setStart(e.target.value)} disabled={!isCustom} className="font-mono" data-testid="report-start" />
      </div>
      <div className="space-y-1.5">
        <Label className="eyebrow">To</Label>
        <Input type="date" min="1900-01-01" value={end} onChange={(e) => setEnd(e.target.value)} disabled={!isCustom} className="font-mono" data-testid="report-end" />
      </div>
      <Button variant="outline" onClick={onRefresh} className="h-9" data-testid="report-refresh">
        <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
      </Button>
    </div>
  );
}

// ---------------- Sales Summary Tab ----------------
function SalesTab({ period, start, end, refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const q = new URLSearchParams({ period, ...(period === "custom" ? { start, end } : {}) });
    api.get(`/reports/sales-summary?${q}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [period, start, end, refreshKey]);

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const t = data.totals;
  const exportExcel = () => {
    const stamp = `${data.range.start}_to_${data.range.end}`;
    downloadCSV(`sales-summary-${stamp}`,
      [
        { key: "number", label: "Invoice #" },
        { key: "date", label: "Date" },
        { key: "customer_name", label: "Customer" },
        { key: "grand_total", label: "Amount" },
        { key: "paid_amount", label: "Paid" },
        { key: "balance_due", label: "Balance" },
        { key: "status", label: "Status" },
        { key: "payment_mode", label: "Payment Mode" },
        { key: "supporting_company", label: "Supporting Company Name" },
      ],
      data.details.map((r) => ({
        ...r,
        supporting_company: (r.supporting_tenders || []).join(" | "),
      })),
    );
  };
  return (
    <div className="space-y-6">
      <ExportBar title={`Sales Summary · ${data.range.start} to ${data.range.end}`} onExcel={exportExcel} testidPrefix="sales-summary" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Total Sales" value={inr(t.sales)} icon={CurrencyInr} foot={`${t.invoice_count} invoice(s)`} />
        <Kpi label="Avg Invoice" value={inr(t.avg_invoice)} />
        <Kpi label="Total Paid" value={inr(t.paid)} color="hsl(var(--success))" />
        <Kpi label="Balance Due" value={inr(t.balance_due)} color={t.balance_due > 0 ? "hsl(var(--destructive))" : undefined} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Taxable Value" value={inr(t.taxable)} />
        <Kpi label="CGST" value={inr(t.cgst)} />
        <Kpi label="SGST" value={inr(t.sgst)} />
        <Kpi label="IGST" value={inr(t.igst)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card-flat p-6">
          <div className="eyebrow">Sales Trend</div>
          <div className="font-heading text-lg font-semibold mt-0.5 mb-4">Daily Revenue</div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => inr(v)} contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                <Line type="monotone" dataKey="sales" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card-flat p-6">
          <div className="eyebrow">Top Customers</div>
          <div className="font-heading text-lg font-semibold mt-0.5 mb-4">By Revenue</div>
          <div className="space-y-2 text-sm max-h-72 overflow-auto" data-testid="top-customers-list">
            {data.top_customers.slice(0, 8).map((c) => (
              <div key={c.customer_id} className="flex justify-between items-center">
                <div className="truncate mr-2">{c.customer_name || "—"}</div>
                <div className="font-mono text-right">
                  <div>{inr(c.total_sales)}</div>
                  <div className="text-[10px] text-muted-foreground">{c.invoice_count} inv</div>
                </div>
              </div>
            ))}
            {data.top_customers.length === 0 && <div className="text-muted-foreground text-sm">No sales in range.</div>}
          </div>
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="eyebrow">Details</div>
            <div className="font-heading text-lg font-semibold">All Invoices ({data.details.length})</div>
          </div>
        </div>
        <table className="data-table" data-testid="sales-details-table">
          <thead>
            <tr>
              <th>Invoice #</th><th>Date</th><th>Customer</th>
              <th className="num">Amount</th><th className="num">Paid</th><th className="num">Balance</th>
              <th>Status</th><th>Mode</th><th>Supporting Company</th>
            </tr>
          </thead>
          <tbody>
            {data.details.map((r) => (
              <tr key={r.id}>
                <td className="font-mono">{r.number}</td>
                <td className="font-mono">{fmtDate(r.date)}</td>
                <td>{r.customer_name}</td>
                <td className="num font-mono">{inr(r.grand_total)}</td>
                <td className="num font-mono">{inr(r.paid_amount)}</td>
                <td className="num font-mono">{inr(r.balance_due)}</td>
                <td><span className={`badge status-${r.status || "unpaid"}`}>{r.status}</span></td>
                <td className="text-xs">{r.payment_mode || "—"}</td>
                <td className="text-[11px] whitespace-pre-line" title={(r.supporting_tenders || []).join("\n")}>
                  {(r.supporting_tenders || []).length ? (r.supporting_tenders || []).join(", ") : "—"}
                </td>
              </tr>
            ))}
            {data.details.length === 0 && (
              <tr><td colSpan={9} className="text-center text-muted-foreground py-6">No invoices in range.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- Profit Summary Tab ----------------
function ProfitTab({ period, start, end, refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const q = new URLSearchParams({ period, ...(period === "custom" ? { start, end } : {}) });
    api.get(`/reports/profit-summary?${q}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [period, start, end, refreshKey]);

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const t = data.totals;
  const exportExcel = () => {
    const stamp = `${data.range.start}_to_${data.range.end}`;
    const summaryRows = [
      { metric: "Revenue (taxable)", amount: t.revenue },
      { metric: "COGS (taxable)", amount: t.cogs },
      { metric: "Gross Profit", amount: t.gross_profit },
      { metric: "Gross Margin %", amount: t.gross_margin_pct },
      { metric: "Expenses", amount: t.expenses },
      { metric: "Net Profit", amount: t.net_profit },
      { metric: "Net Margin %", amount: t.net_margin_pct },
    ];
    const dailyRows = data.daily;
    // Write two sections in one CSV: summary + daily
    const cols1 = [{ key: "metric", label: "Metric" }, { key: "amount", label: "Amount" }];
    const cols2 = [
      { key: "date", label: "Date" }, { key: "revenue", label: "Revenue" },
      { key: "cogs", label: "COGS" }, { key: "expenses", label: "Expenses" }, { key: "profit", label: "Profit" }
    ];
    // Simple concat approach:
    const combined = [
      ...summaryRows.map((r) => ({ section: "Summary", ...r })),
      ...dailyRows.map((r) => ({ section: "Daily", metric: r.date, amount: r.profit, ...r })),
    ];
    downloadCSV(`profit-summary-${stamp}`,
      [
        { key: "section", label: "Section" },
        { key: "metric", label: "Metric / Date" },
        { key: "revenue", label: "Revenue" },
        { key: "cogs", label: "COGS" },
        { key: "expenses", label: "Expenses" },
        { key: "profit", label: "Profit / Amount", get: (r) => r.profit !== undefined ? r.profit : r.amount },
      ],
      combined
    );
  };
  return (
    <div className="space-y-6">
      <ExportBar title={`Profit Summary · ${data.range.start} to ${data.range.end}`} onExcel={exportExcel} testidPrefix="profit-summary" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Revenue" value={inr(t.revenue)} icon={TrendUp} foot={`${t.sales_count} sale(s)`} />
        <Kpi label="COGS" value={inr(t.cogs)} icon={TrendDown} foot={`${t.purchase_count} purchase(s)`} />
        <Kpi label="Gross Profit" value={inr(t.gross_profit)} color={t.gross_profit >= 0 ? "hsl(var(--success))" : "hsl(var(--destructive))"} foot={`${t.gross_margin_pct}% margin`} />
        <Kpi label="Expenses" value={inr(t.expenses)} color="hsl(var(--warning))" foot={`${t.expense_count} entry(ies)`} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card-flat p-6">
          <div className="eyebrow">Bottom Line</div>
          <div className="font-heading text-lg font-semibold mt-0.5">Net Profit</div>
          <div className="text-4xl font-heading font-bold mt-3" style={{ color: t.net_profit >= 0 ? "hsl(var(--success))" : "hsl(var(--destructive))" }} data-testid="net-profit-value">
            {inr(t.net_profit)}
          </div>
          <div className="text-sm text-muted-foreground mt-1">Net margin: {t.net_margin_pct}%</div>
        </div>
        <div className="card-flat p-6">
          <div className="eyebrow">Formula</div>
          <div className="font-heading text-lg font-semibold mt-0.5">How this is calculated</div>
          <div className="mt-3 space-y-1.5 text-sm">
            {t.sales_returns > 0 && <>
              <div className="flex justify-between"><span className="text-muted-foreground">Sales (taxable)</span><span className="font-mono">{inr(t.gross_sales)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">– Sales Returns</span><span className="font-mono">{inr(t.sales_returns)}</span></div>
            </>}
            <div className="flex justify-between"><span className="text-muted-foreground">Revenue (taxable)</span><span className="font-mono" data-testid="profit-formula-revenue">{inr(t.revenue)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">– Purchases / COGS (taxable)</span><span className="font-mono" data-testid="profit-formula-cogs">{inr(t.cogs)}</span></div>
            <div className="flex justify-between border-t border-border pt-1.5"><span>= Gross Profit</span><span className="font-mono">{inr(t.gross_profit)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">– Expenses (incl. salary)</span><span className="font-mono" data-testid="profit-formula-expenses">{inr(t.expenses)}</span></div>
            <div className="flex justify-between border-t border-border pt-1.5 font-heading font-bold"><span>= Net Profit</span><span className="font-mono">{inr(t.net_profit)}</span></div>
          </div>
        </div>
      </div>

      <div className="card-flat p-6">
        <div className="eyebrow">Daily P&L</div>
        <div className="font-heading text-lg font-semibold mt-0.5 mb-4">Revenue vs Cost vs Profit</div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => inr(v)} contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11, textTransform: "uppercase" }} />
              <Bar dataKey="revenue" fill="hsl(var(--chart-1))" name="Revenue" radius={[3,3,0,0]} />
              <Bar dataKey="cogs" fill="hsl(var(--chart-5))" name="COGS" radius={[3,3,0,0]} />
              <Bar dataKey="expenses" fill="hsl(var(--chart-4))" name="Expenses" radius={[3,3,0,0]} />
              <Bar dataKey="profit" fill="hsl(var(--chart-2))" name="Profit" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ---------------- Expenses Summary Tab ----------------
function ExpensesTab({ period, start, end, refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const q = new URLSearchParams({ period, ...(period === "custom" ? { start, end } : {}) });
    api.get(`/reports/expenses-summary?${q}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [period, start, end, refreshKey]);

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const t = data.totals;
  const exportExcel = () => {
    const stamp = `${data.range.start}_to_${data.range.end}`;
    downloadCSV(`expenses-summary-${stamp}`,
      [
        { key: "date", label: "Date" },
        { key: "category", label: "Category" },
        { key: "description", label: "Description" },
        { key: "vendor_name", label: "Vendor" },
        { key: "reference", label: "Reference" },
        { key: "payment_mode", label: "Payment Mode" },
        { key: "amount", label: "Amount" },
      ],
      data.details
    );
  };
  return (
    <div className="space-y-6">
      <ExportBar title={`Expenses Summary · ${data.range.start} to ${data.range.end}`} onExcel={exportExcel} testidPrefix="expenses-summary" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Total Expenses" value={inr(t.amount)} icon={Wallet} color="hsl(var(--warning))" foot={`${t.count} entry(ies)`} />
        <Kpi label="Avg Entry" value={inr(t.avg)} />
        <Kpi label="Categories" value={data.by_category.length} />
        <Kpi label="Payees" value={(data.by_payee || []).length} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-flat p-6">
          <div className="eyebrow">Breakdown</div>
          <div className="font-heading text-lg font-semibold mt-0.5 mb-4">By Category</div>
          {data.by_category.length === 0 ? (
            <div className="text-sm text-muted-foreground">No expenses in range.</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.by_category} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={90} label={(e) => e.category}>
                    {data.by_category.map((slice, i) => <Cell key={slice?.name || `slice-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => inr(v)} contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="card-flat p-6">
          <div className="eyebrow">Detail Table</div>
          <div className="font-heading text-lg font-semibold mt-0.5 mb-4">By Category</div>
          <table className="data-table">
            <thead>
              <tr><th>Category</th><th className="num">Count</th><th className="num">Amount</th></tr>
            </thead>
            <tbody>
              {data.by_category.map((r) => (
                <tr key={r.category}>
                  <td>{r.category}</td>
                  <td className="num font-mono">{r.count}</td>
                  <td className="num font-mono">{inr(r.amount)}</td>
                </tr>
              ))}
              {data.by_category.length === 0 && <tr><td colSpan={3} className="text-center text-muted-foreground py-4">—</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <div className="p-4 border-b border-border">
          <div className="eyebrow">Details</div>
          <div className="font-heading text-lg font-semibold">All Expenses ({data.details.length})</div>
        </div>
        <table className="data-table" data-testid="expenses-details-table">
          <thead>
            <tr><th>Date</th><th>Category</th><th>Description</th><th>Vendor / Payee</th><th>Ref</th><th>For Month</th><th>Mode</th><th className="num">Amount</th></tr>
          </thead>
          <tbody>
            {data.details.map((r) => (
              <tr key={r.id}>
                <td className="font-mono">{fmtDate(r.date)}</td>
                <td>{r.category}</td>
                <td className="text-xs">{r.description || "—"}</td>
                <td>{r.vendor_name || "—"}</td>
                <td className="font-mono text-xs">{r.reference || "—"}</td>
                <td className="font-mono text-xs">
                  {r.salary_month
                    ? new Date(r.salary_month + "-01").toLocaleDateString("en-IN", { month: "short", year: "numeric" })
                    : "—"}
                </td>
                <td className="text-xs">{r.payment_mode || "—"}</td>
                <td className="num font-mono">{inr(r.amount)}</td>
              </tr>
            ))}
            {data.details.length === 0 && (
              <tr><td colSpan={8} className="text-center text-muted-foreground py-6">No expenses in range.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(data.by_payee || []).length > 0 && (
        <div className="card-flat overflow-hidden" data-testid="expenses-by-payee-card">
          <div className="p-4 border-b border-border">
            <div className="eyebrow">Breakdown</div>
            <div className="font-heading text-lg font-semibold">By Payee ({(data.by_payee || []).length})</div>
          </div>
          <table className="data-table" data-testid="expenses-by-payee-table">
            <thead><tr><th>Payee</th><th className="num">Count</th><th className="num">Amount</th></tr></thead>
            <tbody>
              {(data.by_payee || []).map((r) => (
                <tr key={r.payee}>
                  <td className="font-medium">{r.payee}</td>
                  <td className="num font-mono">{r.count}</td>
                  <td className="num font-mono font-semibold">{inr(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------- Customer Statement Tab ----------------
function CustomerStatementTab({ period, start, end, refreshKey }) {
  const { activeCompany } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("all");
  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams({ period, ...(period === "custom" ? { start, end } : {}) });
    if (customerId === "all") {
      setDetail(null);
      api.get(`/ledger/customers-summary?${q}`)
        .then((r) => setSummary(r.data))
        .catch((e) => toast.error(formatApiError(e)))
        .finally(() => setLoading(false));
    } else {
      setSummary(null);
      api.get(`/ledger/customer/${customerId}`)
        .then((r) => setDetail(r.data))
        .catch((e) => toast.error(formatApiError(e)))
        .finally(() => setLoading(false));
    }
  }, [customerId, period, start, end, refreshKey]);

  const filteredDetail = useMemo(() => {
    if (!detail) return null;
    const src = detail.entries.filter((e) => {
      if (period === "all") return true;
      const d = (e.date || "").slice(0, 10);
      const s = period === "custom" ? start : "";
      const en = period === "custom" ? end : "";
      // For non-custom period, backend doesn't scope, so filter here using resolved range
      if (period === "custom") return (!s || d >= s) && (!en || d <= en);
      return true;
    });
    let debit = 0, credit = 0;
    src.forEach((e) => { debit += Number(e.debit || 0); credit += Number(e.credit || 0); });
    return { entries: src, totals: { debit, credit, balance: debit - credit } };
  }, [detail, period, start, end]);

  const exportExcel = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    if (customerId === "all" && summary) {
      downloadCSV(`customer-statement-all-${stamp}`,
        [
          { key: "customer_name", label: "Customer" },
          { key: "phone", label: "Phone" },
          { key: "gstin", label: "GSTIN" },
          { key: "invoiced_count", label: "Invoices" },
          { key: "temp_invoice_count", label: "Temp Invoices" },
          { key: "receipt_count", label: "Receipts" },
          { key: "total_debit", label: "Debit (Invoiced + Pending)" },
          { key: "total_credit", label: "Credit (Received)" },
          { key: "pending_balance", label: "Pending (Inv + Temp)" },
          { key: "last_transaction", label: "Last Txn" },
        ],
        summary.rows);
    } else if (detail && filteredDetail) {
      const rows = filteredDetail.entries.map((e) => ({
        date: e.date, ref: e.ref, description: e.description,
        debit: e.debit || 0, credit: e.credit || 0, balance: e.balance,
      }));
      downloadCSV(`customer-statement-${(detail.party.name || "").replace(/\s+/g, "-")}-${stamp}`,
        [
          { key: "date", label: "Date" },
          { key: "ref", label: "Ref" },
          { key: "description", label: "Description" },
          { key: "debit", label: "Debit" },
          { key: "credit", label: "Credit" },
          { key: "balance", label: "Balance" },
        ], rows);
    }
  };

  const printStatement = () => {
    const fmtInr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const periodLabel = period === "custom" ? `${start} → ${end}` : period.replace("_", " ");
    if (customerId === "all" && summary?.rows?.length) {
      const totalDebit = summary.rows.reduce((s, r) => s + Number(r.total_debit || 0), 0);
      const totalCredit = summary.rows.reduce((s, r) => s + Number(r.total_credit || 0), 0);
      const totalPending = summary.rows.reduce((s, r) => s + Number(r.pending_balance || 0), 0);
      openPrintStatement({
        title: `Customer Statements · ${periodLabel}`,
        company: activeCompany || {},
        sections: [{
          heading: "All Customers Summary",
          subheading: `${summary.rows.length} customer(s)`,
          columns: [
            { key: "customer_name", label: "Customer" },
            { key: "phone", label: "Phone", mono: true },
            { key: "invoiced", label: "Invoices", align: "right" },
            { key: "temp", label: "Temp Inv", align: "right" },
            { key: "debit", label: "Debit (Inv + Temp)", align: "right", mono: true },
            { key: "credit", label: "Credit (Received)", align: "right", mono: true, tone: "pos" },
            { key: "pending", label: "Pending", align: "right", mono: true, tone: "neg" },
          ],
          rows: summary.rows.map((r) => ({
            customer_name: r.customer_name, phone: r.phone || "—",
            invoiced: r.invoiced_count,
            temp: r.temp_invoice_count || 0,
            debit: fmtInr(r.total_debit),
            credit: fmtInr(r.total_credit), pending: fmtInr(r.pending_balance),
          })),
        }],
        totals: [
          { label: "Total Debit", value: fmtInr(totalDebit) },
          { label: "Total Credit", value: fmtInr(totalCredit), tone: "pos" },
          { label: "Net Pending", value: fmtInr(totalPending), tone: "neg" },
        ],
        note: "Please reconcile the amounts above against your books and clear any pending balances.",
      });
    } else if (detail && filteredDetail) {
      const cust = detail.party;
      const subheader = [
        cust.gstin ? `GSTIN <span class="mono">${cust.gstin}</span>` : "",
        cust.phone ? `Mobile <span class="mono">${cust.phone}</span>` : "",
        cust.address ? cust.address : "",
      ].filter(Boolean).join(" · ");
      openPrintStatement({
        title: `Statement of Account · ${periodLabel}`,
        company: activeCompany || {},
        sections: [{
          heading: cust.name,
          subheading: subheader,
          columns: [
            { key: "date", label: "Date", mono: true },
            { key: "ref", label: "Ref", mono: true },
            { key: "description", label: "Description" },
            { key: "debit", label: "Debit", align: "right", mono: true },
            { key: "credit", label: "Credit", align: "right", mono: true, tone: "pos" },
            { key: "balance", label: "Running Balance", align: "right", mono: true },
          ],
          rows: filteredDetail.entries.map((e) => ({
            date: e.date, ref: e.ref || "—", description: e.description || "",
            debit: e.debit ? fmtInr(e.debit) : "",
            credit: e.credit ? fmtInr(e.credit) : "",
            balance: fmtInr(e.balance),
          })),
        }],
        totals: [
          { label: "Total Debit", value: fmtInr(filteredDetail.totals.debit) },
          { label: "Total Credit", value: fmtInr(filteredDetail.totals.credit), tone: "pos" },
          {
            label: filteredDetail.totals.balance >= 0 ? "Amount Due to Us" : "Advance Received",
            value: fmtInr(Math.abs(filteredDetail.totals.balance)),
            tone: filteredDetail.totals.balance >= 0 ? "neg" : "pos",
          },
        ],
        note: "This statement reflects transactions on the selected period. Please contact us for any discrepancy.",
      });
    } else {
      toast.error("Nothing to print — pick a customer or period with entries");
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6">
      <ExportBar
        title="Customer Statement — Debit / Credit / Pending"
        onExcel={exportExcel}
        extraButtons={
          <Button variant="outline" size="sm" className="h-8" onClick={printStatement} data-testid="customer-statement-print">
            <Printer size={14} className="mr-1.5" /> Print Statement
          </Button>
        }
        testidPrefix="customer-statement"
      />

      <div className="card-flat p-4 flex flex-wrap items-end gap-3 no-print">
        <div className="space-y-1.5 min-w-[260px]">
          <Label className="eyebrow">Customer</Label>
          <Select value={customerId} onValueChange={setCustomerId}>
            <SelectTrigger data-testid="cs-customer-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customers (Summary)</SelectItem>
              {customers.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-[11px] text-muted-foreground ml-auto">
          Debit = invoiced · Credit = received / returns · Pending = balance due
        </div>
      </div>

      {customerId === "all" && summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi label="Customers" value={summary.totals.customer_count} icon={FileText} />
            <Kpi label="Debit (Invoiced)" value={inr(summary.totals.total_debit)} color="hsl(var(--warning))" />
            <Kpi label="Credit (Received)" value={inr(summary.totals.total_credit)} color="hsl(var(--success))" />
            <Kpi label="Total Pending" value={inr(summary.totals.total_pending)} color={summary.totals.total_pending > 0 ? "hsl(var(--destructive))" : undefined} />
          </div>

          <div className="card-flat overflow-hidden">
            <div className="p-4 border-b border-border">
              <div className="eyebrow">Details</div>
              <div className="font-heading text-lg font-semibold">Per-Customer Statement ({summary.rows.length})</div>
            </div>
            <table className="data-table" data-testid="cs-summary-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th className="num">Invoices</th>
                  <th className="num">Receipts</th>
                  <th className="num">Debit</th>
                  <th className="num">Credit</th>
                  <th className="num">Pending</th>
                  <th>Last Txn</th>
                </tr>
              </thead>
              <tbody>
                {summary.rows.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">No customer activity in the selected range.</td></tr>
                ) : summary.rows.map((r) => (
                  <tr key={r.customer_id} data-testid={`cs-row-${r.customer_id}`}>
                    <td className="font-medium">{r.customer_name}</td>
                    <td className="font-mono text-[12px]">{r.phone || "—"}</td>
                    <td className="num font-mono">{r.invoiced_count}</td>
                    <td className="num font-mono">{r.receipt_count}</td>
                    <td className="num font-mono">{inr(r.total_debit)}</td>
                    <td className="num font-mono" style={{ color: "hsl(var(--success))" }}>{inr(r.total_credit)}</td>
                    <td className="num font-mono font-semibold" style={{ color: r.pending_balance > 0 ? "hsl(var(--destructive))" : undefined }}>
                      {inr(r.pending_balance)}
                    </td>
                    <td className="font-mono text-[12px]">{r.last_transaction || "—"}</td>
                  </tr>
                ))}
                {summary.rows.length > 0 && (
                  <tr className="bg-secondary/60 font-semibold">
                    <td colSpan={4} className="text-right">Totals</td>
                    <td className="num font-mono">{inr(summary.totals.total_debit)}</td>
                    <td className="num font-mono">{inr(summary.totals.total_credit)}</td>
                    <td className="num font-mono">{inr(summary.totals.total_pending)}</td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {customerId !== "all" && detail && filteredDetail && (
        <>
          <div className="card-flat p-4 flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="eyebrow">Customer</div>
              <div className="font-heading text-xl font-semibold">{detail.party.name}</div>
              <div className="text-[12px] text-muted-foreground mt-1 font-mono">
                {detail.party.gstin && <>GSTIN: {detail.party.gstin} · </>}
                {detail.party.phone && <>{detail.party.phone}</>}
                {detail.party.email && <> · {detail.party.email}</>}
              </div>
            </div>
            {detail.party.phone && (
              <Button variant="outline" className="h-9 no-print"
                data-testid="cs-whatsapp-send"
                onClick={() => {
                  const bal = Math.max(0, detail.totals.balance);
                  const advance = Math.max(0, -detail.totals.balance);
                  const waSelf = (activeCompany?.whatsapp_number || "").trim();
                  const lines = [
                    `Hi ${detail.party.name}, greetings from your account team.`,
                    ``,
                    `Statement summary as of ${new Date().toISOString().slice(0,10)}:`,
                    `• Total Invoiced: ${inr(detail.totals.debit)}`,
                    `• Total Received: ${inr(detail.totals.credit)}`,
                    bal > 0 ? `• Amount Due: ${inr(bal)}` : advance > 0 ? `• Advance on account: ${inr(advance)}` : `• All Settled — thank you!`,
                    ``,
                    bal > 0 ? `Kindly arrange payment at your earliest convenience.` : `Thank you for your business.`,
                    ...(waSelf ? [``, `WhatsApp us: ${waSelf}`] : []),
                  ];
                  const text = encodeURIComponent(lines.join("\n"));
                  const phone = (detail.party.phone || "").replace(/[^\d+]/g, "").replace(/^\+/, "");
                  const num = phone.length === 10 ? `91${phone}` : phone;
                  window.open(`https://wa.me/${num}?text=${text}`, "_blank");
                }}>
                <WhatsappLogo size={16} weight="fill" className="mr-1.5" style={{ color: "#25D366" }} />
                Send Statement on WhatsApp
              </Button>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi label="Debit (Invoiced)" value={inr(filteredDetail.totals.debit)} color="hsl(var(--warning))" />
            <Kpi label="Credit (Received)" value={inr(filteredDetail.totals.credit)} color="hsl(var(--success))" />
            <Kpi label="Period Net" value={inr(filteredDetail.totals.balance)} />
            <Kpi label="Total Pending"
              value={inr(Math.max(0, detail.totals.balance))}
              color={detail.totals.balance > 0 ? "hsl(var(--destructive))" : undefined}
              foot={detail.totals.balance > 0 ? "Receivable" : detail.totals.balance < 0 ? "Advance" : "Settled"} />
          </div>

          <div className="card-flat overflow-hidden">
            <div className="p-4 border-b border-border">
              <div className="eyebrow">History</div>
              <div className="font-heading text-lg font-semibold">All Entries ({filteredDetail.entries.length})</div>
            </div>
            <table className="data-table" data-testid="cs-detail-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Ref</th>
                  <th>Description</th>
                  <th className="num">Debit</th>
                  <th className="num">Credit</th>
                  <th className="num">Balance</th>
                </tr>
              </thead>
              <tbody>
                {filteredDetail.entries.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No entries in the selected range.</td></tr>
                ) : filteredDetail.entries.map((e, i) => (
                  <tr key={e.id || e.voucher_id || `${e.date}-${e.number || i}`}>
                    <td className="font-mono text-[12px]">{e.date}</td>
                    <td className="font-mono text-[12px]">{e.ref}</td>
                    <td>{e.description}</td>
                    <td className="num font-mono">{e.debit ? inr(e.debit) : "—"}</td>
                    <td className="num font-mono">{e.credit ? inr(e.credit) : "—"}</td>
                    <td className="num font-mono font-semibold">
                      {inr(Math.abs(e.balance))} {e.balance !== 0 && (e.balance > 0 ? "Dr" : "Cr")}
                    </td>
                  </tr>
                ))}
                <tr className="bg-secondary/60 font-semibold">
                  <td colSpan={3}>Period Total</td>
                  <td className="num font-mono">{inr(filteredDetail.totals.debit)}</td>
                  <td className="num font-mono">{inr(filteredDetail.totals.credit)}</td>
                  <td className="num font-mono">
                    {inr(Math.abs(filteredDetail.totals.balance))} {filteredDetail.totals.balance !== 0 && (filteredDetail.totals.balance > 0 ? "Dr" : "Cr")}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}


// ---------------- Suppliers Details Tab ----------------
function SuppliersTab({ period, start, end, refreshKey }) {
  const { activeCompany } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    const params = new URLSearchParams();
    if (start && end) { params.set("start", start); params.set("end", end); }
    api.get(`/reports/suppliers-details?${params.toString()}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [start, end, refreshKey]);
  const rows = useMemo(() => {
    const list = data?.rows || [];
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((r) => `${r.name} ${r.gstin} ${r.phone} ${r.email} ${r.state}`.toLowerCase().includes(s));
  }, [data, q]);
  const doExport = () => {
    if (!data) return;
    const stamp = new Date().toISOString().slice(0, 10);
    const cols = [
      { key: "name", label: "Supplier" },
      { key: "gstin", label: "GSTIN" },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "state", label: "State" },
      { key: "state_code", label: "State Code" },
      { key: "address", label: "Address" },
      { key: "invoice_count", label: "# Bills" },
      { key: "total_purchases", label: "Total Purchases" },
      { key: "total_paid", label: "Total Paid" },
      { key: "outstanding", label: "Outstanding" },
      { key: "opening_balance", label: "Opening Balance" },
      { key: "last_purchase_date", label: "Last Bill Date" },
    ];
    downloadCSV(`suppliers-details-${stamp}`, cols, rows);
  };
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const T = data.totals || {};
  return (
    <div className="space-y-4" data-testid="suppliers-report">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="card-flat p-4"><div className="eyebrow">Suppliers</div><div className="font-heading text-2xl font-bold mt-1">{T.supplier_count || 0}</div></div>
        <div className="card-flat p-4"><div className="eyebrow"># Bills</div><div className="font-heading text-2xl font-bold mt-1">{T.invoice_count || 0}</div></div>
        <div className="card-flat p-4"><div className="eyebrow">Purchases</div><div className="font-heading text-xl font-bold mt-1 font-mono">{inr(T.purchases || 0)}</div></div>
        <div className="card-flat p-4"><div className="eyebrow">Paid</div><div className="font-heading text-xl font-bold mt-1 font-mono" style={{color:"hsl(var(--success))"}}>{inr(T.paid || 0)}</div></div>
        <div className="card-flat p-4"><div className="eyebrow">Outstanding</div><div className="font-heading text-xl font-bold mt-1 font-mono" style={{color:"hsl(var(--warning))"}}>{inr(T.outstanding || 0)}</div></div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Input placeholder="Search supplier / GSTIN / phone / email…" value={q} onChange={(e) => setQ(e.target.value)}
          className="max-w-md" data-testid="suppliers-report-search" />
        <div className="text-[11px] text-muted-foreground font-mono ml-auto">{rows.length} of {data.rows.length} shown</div>
        <Button variant="outline" size="sm" onClick={doExport} data-testid="suppliers-report-export">Export CSV</Button>
      </div>
      <div className="card-flat overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Supplier</th>
              <th>GSTIN</th>
              <th>Phone</th>
              <th>Email</th>
              <th>State</th>
              <th className="num"># Bills</th>
              <th className="num">Purchases</th>
              <th className="num">Paid</th>
              <th className="num">Outstanding</th>
              <th>Last Bill</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={10} className="text-center py-10 text-muted-foreground">
                {data.rows.length === 0 ? "No suppliers on record yet." : `No supplier matches "${q}".`}
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`supplier-row-${r.id}`}>
                <td className="font-medium">{r.name}</td>
                <td className="font-mono text-[11.5px]">{r.gstin || "—"}</td>
                <td className="font-mono text-[11.5px]">{r.phone || "—"}</td>
                <td className="font-mono text-[11.5px]">{r.email || "—"}</td>
                <td className="text-[12px]">{r.state || "—"}{r.state_code ? ` (${r.state_code})` : ""}</td>
                <td className="num">{r.invoice_count}</td>
                <td className="num font-mono">{inr(r.total_purchases)}</td>
                <td className="num font-mono" style={{color: r.total_paid > 0 ? "hsl(var(--success))" : undefined}}>{inr(r.total_paid)}</td>
                <td className="num font-mono font-semibold" style={{color: r.outstanding > 0.005 ? "hsl(var(--warning))" : undefined}}>{inr(r.outstanding)}</td>
                <td className="font-mono text-[11.5px]">{r.last_purchase_date ? fmtDate(r.last_purchase_date) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ---------------- Pending Payments Tab ----------------
function PendingPaymentsTab({ refreshKey }) {
  const { activeCompany } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/reports/pending-payments")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [refreshKey]);

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const t = data.totals;
  const exportExcel = () => {
    const cols = [
      { key: "type", label: "Type" },
      { key: "number", label: "Invoice #" },
      { key: "date", label: "Date" },
      { key: "due_date", label: "Due Date" },
      { key: "party_name", label: "Party" },
      { key: "grand_total", label: "Total" },
      { key: "paid_amount", label: "Paid" },
      { key: "balance_due", label: "Balance" },
      { key: "days_overdue", label: "Days Overdue" },
      { key: "status", label: "Status" },
      { key: "supporting_company", label: "Supporting Company Name" },
    ];
    const rows = [
      ...data.receivables.details.map((r) => ({
        ...r, type: "Receivable",
        supporting_company: (r.supporting_tenders || []).join(" | "),
      })),
      ...data.payables.details.map((r) => ({
        ...r, type: "Payable",
        supporting_company: (r.supporting_tenders || []).join(" | "),
      })),
      ...(data.temp_invoices?.details || []).map((r) => ({
        type: "Temp Invoice",
        number: r.number,
        date: r.date,
        due_date: "",
        party_name: r.customer_name,
        grand_total: r.grand_total,
        paid_amount: r.received_amount,
        balance_due: r.balance_amount,
        days_overdue: r.age_days,
        status: r.status,
        supporting_company: (r.supporting_tenders || []).join(" | "),
      })),
    ];
    downloadCSV(`pending-payments-${new Date().toISOString().slice(0,10)}`, cols, rows);
  };
  const temps = data.temp_invoices || { summary: [], details: [], aging: { "0_10":0,"10_20":0,"20_30":0,"30_plus":0 } };

  const printStatement = () => {
    const fmtInr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const sections = [];
    if (data.receivables.details.length) {
      sections.push({
        heading: "Receivables (Customer Dues)",
        subheading: `${data.receivables.details.length} invoice(s) · Total <b>${fmtInr(t.total_receivable)}</b>`,
        columns: [
          { key: "number", label: "Invoice #", mono: true },
          { key: "date", label: "Date", mono: true },
          { key: "party_name", label: "Customer" },
          { key: "grand_total", label: "Total", align: "right", mono: true },
          { key: "balance_due", label: "Balance", align: "right", mono: true, tone: "neg" },
          { key: "days_overdue", label: "Overdue", align: "right", mono: true },
        ],
        rows: data.receivables.details.map((r) => ({
          number: r.number, date: r.date, party_name: r.party_name,
          grand_total: fmtInr(r.grand_total), balance_due: fmtInr(r.balance_due),
          days_overdue: r.days_overdue > 0 ? `${r.days_overdue}d` : "—",
        })),
      });
    }
    if (data.payables.details.length) {
      sections.push({
        heading: "Payables (Supplier Dues)",
        subheading: `${data.payables.details.length} invoice(s) · Total <b>${fmtInr(t.total_payable)}</b>`,
        columns: [
          { key: "number", label: "Invoice #", mono: true },
          { key: "date", label: "Date", mono: true },
          { key: "party_name", label: "Supplier" },
          { key: "grand_total", label: "Total", align: "right", mono: true },
          { key: "balance_due", label: "Balance", align: "right", mono: true, tone: "neg" },
          { key: "days_overdue", label: "Overdue", align: "right", mono: true },
        ],
        rows: data.payables.details.map((r) => ({
          number: r.number, date: r.date, party_name: r.party_name,
          grand_total: fmtInr(r.grand_total), balance_due: fmtInr(r.balance_due),
          days_overdue: r.days_overdue > 0 ? `${r.days_overdue}d` : "—",
        })),
      });
    }
    if (temps.details.length) {
      sections.push({
        heading: "Payment Pending (Temp Invoices)",
        subheading: `${temps.details.length} temp invoice(s) · Outstanding <b>${fmtInr(t.total_temp_outstanding || 0)}</b>`,
        columns: [
          { key: "number", label: "Temp #", mono: true },
          { key: "date", label: "Date", mono: true },
          { key: "customer_name", label: "Customer" },
          { key: "grand_total", label: "Total", align: "right", mono: true },
          { key: "received", label: "Received", align: "right", mono: true, tone: "pos" },
          { key: "balance", label: "Balance", align: "right", mono: true, tone: "neg" },
          { key: "age", label: "Age", align: "right", mono: true },
        ],
        rows: temps.details.map((r) => ({
          number: r.number, date: r.date, customer_name: r.customer_name,
          grand_total: fmtInr(r.grand_total), received: fmtInr(r.received_amount),
          balance: fmtInr(r.balance_amount), age: `${r.age_days}d`,
        })),
      });
    }
    const res = openPrintStatement({
      title: "Pending Payments Statement",
      company: activeCompany || {},
      sections,
      totals: [
        { label: "Total Receivable", value: fmtInr(t.total_receivable), tone: "pos" },
        { label: "Total Payable", value: fmtInr(t.total_payable), tone: "neg" },
        { label: "Temp Invoice Dues", value: fmtInr(t.total_temp_outstanding || 0), tone: "neg" },
        { label: "Net Position", value: fmtInr(t.net_position), tone: t.net_position >= 0 ? "pos" : "neg" },
      ],
      note: "Please review and reconcile the pending amounts above.",
    });
    if (!res.ok) toast.error(res.error);
  };

  return (
    <div className="space-y-6">
      <ExportBar title="Pending Payments · Receivables + Payables + Temp Invoices" onExcel={exportExcel} extraButtons={
        <Button variant="outline" className="h-9" onClick={printStatement} data-testid="pending-payments-print">
          <Printer size={16} className="mr-1.5" /> Print Statement
        </Button>
      } testidPrefix="pending-payments" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Total Receivable" value={inr(t.total_receivable)} icon={TrendUp} color="hsl(var(--success))" foot={`${t.receivable_invoice_count} invoice(s)`} />
        <Kpi label="Total Payable" value={inr(t.total_payable)} icon={TrendDown} color="hsl(var(--warning))" foot={`${t.payable_invoice_count} invoice(s)`} />
        <Kpi label="Temp Invoice Dues" value={inr(t.total_temp_outstanding || 0)} icon={Warning} color="hsl(var(--warning))" foot={`${t.temp_invoice_count || 0} temp(s)`} />
        <Kpi label="Net Position" value={inr(t.net_position)} color={t.net_position >= 0 ? "hsl(var(--success))" : "hsl(var(--destructive))"} />
      </div>

      <Tabs defaultValue="receivables">
        <TabsList>
          <TabsTrigger value="receivables" data-testid="pp-tab-receivables">Receivables (Customer Dues)</TabsTrigger>
          <TabsTrigger value="payables" data-testid="pp-tab-payables">Payables (Supplier Dues)</TabsTrigger>
          <TabsTrigger value="temps" data-testid="pp-tab-temps">Payment Pending (Temp Invoices)</TabsTrigger>
        </TabsList>

        <TabsContent value="receivables" className="space-y-4 mt-4">
          <div className="card-flat p-6">
            <div className="eyebrow">Ageing</div>
            <div className="font-heading text-lg font-semibold mt-0.5 mb-4">Overdue Buckets</div>
            <div className="grid grid-cols-4 gap-3">
              <div className="rounded border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">0–30 days</div><div className="font-mono text-lg font-semibold">{inr(data.receivables.aging["0_30"])}</div></div>
              <div className="rounded border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">31–60 days</div><div className="font-mono text-lg font-semibold">{inr(data.receivables.aging["31_60"])}</div></div>
              <div className="rounded border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">61–90 days</div><div className="font-mono text-lg font-semibold" style={{ color: "hsl(var(--warning))" }}>{inr(data.receivables.aging["61_90"])}</div></div>
              <div className="rounded border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">90+ days</div><div className="font-mono text-lg font-semibold" style={{ color: "hsl(var(--destructive))" }}>{inr(data.receivables.aging["90_plus"])}</div></div>
            </div>
          </div>

          <div className="card-flat overflow-hidden">
            <div className="p-4 border-b border-border">
              <div className="eyebrow">Summary</div>
              <div className="font-heading text-lg font-semibold">By Customer ({data.receivables.summary.length})</div>
            </div>
            <table className="data-table" data-testid="receivables-summary-table">
              <thead><tr><th>Customer</th><th className="num">Invoices</th><th className="num">Outstanding</th><th className="num">Oldest Overdue</th></tr></thead>
              <tbody>
                {data.receivables.summary.map((r) => (
                  <tr key={r.party_id}>
                    <td>{r.party_name}</td>
                    <td className="num font-mono">{r.invoice_count}</td>
                    <td className="num font-mono">{inr(r.total_outstanding)}</td>
                    <td className="num font-mono">{r.oldest_days_overdue > 0 ? `${r.oldest_days_overdue}d` : "—"}</td>
                  </tr>
                ))}
                {data.receivables.summary.length === 0 && <tr><td colSpan={4} className="text-center text-muted-foreground py-6">All customer invoices are paid.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="card-flat overflow-hidden">
            <div className="p-4 border-b border-border">
              <div className="eyebrow">Details</div>
              <div className="font-heading text-lg font-semibold">Every Unpaid Invoice</div>
            </div>
            <table className="data-table" data-testid="receivables-details-table">
              <thead><tr><th>Invoice</th><th>Date</th><th>Due</th><th>Customer</th><th className="num">Total</th><th className="num">Paid</th><th className="num">Balance</th><th className="num">Overdue</th></tr></thead>
              <tbody>
                {data.receivables.details.map((r) => (
                  <tr key={r.id}>
                    <td className="font-mono">{r.number}</td>
                    <td className="font-mono">{fmtDate(r.date)}</td>
                    <td className="font-mono">{r.due_date || "—"}</td>
                    <td>{r.party_name}</td>
                    <td className="num font-mono">{inr(r.grand_total)}</td>
                    <td className="num font-mono">{inr(r.paid_amount)}</td>
                    <td className="num font-mono">{inr(r.balance_due)}</td>
                    <td className="num font-mono" style={r.days_overdue > 0 ? { color: r.days_overdue > 60 ? "hsl(var(--destructive))" : "hsl(var(--warning))" } : {}}>{r.days_overdue > 0 ? `${r.days_overdue}d` : "—"}</td>
                  </tr>
                ))}
                {data.receivables.details.length === 0 && <tr><td colSpan={8} className="text-center text-muted-foreground py-6">Nothing pending.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="payables" className="space-y-4 mt-4">
          <div className="card-flat p-6">
            <div className="eyebrow">Ageing</div>
            <div className="font-heading text-lg font-semibold mt-0.5 mb-4">Overdue Buckets</div>
            <div className="grid grid-cols-4 gap-3">
              <div className="rounded border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">0–30 days</div><div className="font-mono text-lg font-semibold">{inr(data.payables.aging["0_30"])}</div></div>
              <div className="rounded border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">31–60 days</div><div className="font-mono text-lg font-semibold">{inr(data.payables.aging["31_60"])}</div></div>
              <div className="rounded border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">61–90 days</div><div className="font-mono text-lg font-semibold" style={{ color: "hsl(var(--warning))" }}>{inr(data.payables.aging["61_90"])}</div></div>
              <div className="rounded border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">90+ days</div><div className="font-mono text-lg font-semibold" style={{ color: "hsl(var(--destructive))" }}>{inr(data.payables.aging["90_plus"])}</div></div>
            </div>
          </div>

          <div className="card-flat overflow-hidden">
            <div className="p-4 border-b border-border">
              <div className="eyebrow">Summary</div>
              <div className="font-heading text-lg font-semibold">By Supplier ({data.payables.summary.length})</div>
            </div>
            <table className="data-table" data-testid="payables-summary-table">
              <thead><tr><th>Supplier</th><th className="num">Invoices</th><th className="num">Outstanding</th><th className="num">Oldest Overdue</th></tr></thead>
              <tbody>
                {data.payables.summary.map((r) => (
                  <tr key={r.party_id}>
                    <td>{r.party_name}</td>
                    <td className="num font-mono">{r.invoice_count}</td>
                    <td className="num font-mono">{inr(r.total_outstanding)}</td>
                    <td className="num font-mono">{r.oldest_days_overdue > 0 ? `${r.oldest_days_overdue}d` : "—"}</td>
                  </tr>
                ))}
                {data.payables.summary.length === 0 && <tr><td colSpan={4} className="text-center text-muted-foreground py-6">All supplier invoices are cleared.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="card-flat overflow-hidden">
            <div className="p-4 border-b border-border">
              <div className="eyebrow">Details</div>
              <div className="font-heading text-lg font-semibold">Every Unpaid Purchase</div>
            </div>
            <table className="data-table" data-testid="payables-details-table">
              <thead><tr><th>Invoice</th><th>Date</th><th>Due</th><th>Supplier</th><th className="num">Total</th><th className="num">Paid</th><th className="num">Balance</th><th className="num">Overdue</th></tr></thead>
              <tbody>
                {data.payables.details.map((r) => (
                  <tr key={r.id}>
                    <td className="font-mono">{r.number}</td>
                    <td className="font-mono">{fmtDate(r.date)}</td>
                    <td className="font-mono">{r.due_date || "—"}</td>
                    <td>{r.party_name}</td>
                    <td className="num font-mono">{inr(r.grand_total)}</td>
                    <td className="num font-mono">{inr(r.paid_amount)}</td>
                    <td className="num font-mono">{inr(r.balance_due)}</td>
                    <td className="num font-mono" style={r.days_overdue > 0 ? { color: r.days_overdue > 60 ? "hsl(var(--destructive))" : "hsl(var(--warning))" } : {}}>{r.days_overdue > 0 ? `${r.days_overdue}d` : "—"}</td>
                  </tr>
                ))}
                {data.payables.details.length === 0 && <tr><td colSpan={8} className="text-center text-muted-foreground py-6">Nothing pending.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="temps" className="space-y-4 mt-4">
          <div className="card-flat p-6">
            <div className="eyebrow">Aging · Temp Invoices</div>
            <div className="font-heading text-lg font-semibold mt-0.5 mb-4">By Age Since Delivery</div>
            <div className="grid grid-cols-4 gap-3">
              <div className="rounded border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">0–10 days</div><div className="font-mono text-lg font-semibold">{inr(temps.aging["0_10"])}</div></div>
              <div className="rounded border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">10–20 days</div><div className="font-mono text-lg font-semibold" style={{ color: "hsl(var(--warning))" }}>{inr(temps.aging["10_20"])}</div></div>
              <div className="rounded border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">20–30 days</div><div className="font-mono text-lg font-semibold" style={{ color: "hsl(var(--warning))" }}>{inr(temps.aging["20_30"])}</div></div>
              <div className="rounded border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">30+ days</div><div className="font-mono text-lg font-semibold" style={{ color: "hsl(var(--destructive))" }}>{inr(temps.aging["30_plus"])}</div></div>
            </div>
          </div>

          <div className="card-flat overflow-hidden">
            <div className="p-4 border-b border-border">
              <div className="eyebrow">Summary</div>
              <div className="font-heading text-lg font-semibold">By Customer ({temps.summary.length})</div>
            </div>
            <table className="data-table" data-testid="temps-summary-table">
              <thead><tr><th>Customer</th><th className="num">Temp Invoices</th><th className="num">Outstanding</th><th className="num">Oldest</th></tr></thead>
              <tbody>
                {temps.summary.map((r) => (
                  <tr key={r.customer_id}>
                    <td>{r.customer_name}</td>
                    <td className="num font-mono">{r.count}</td>
                    <td className="num font-mono">{inr(r.outstanding)}</td>
                    <td className="num font-mono">{r.oldest_age_days}d</td>
                  </tr>
                ))}
                {temps.summary.length === 0 && <tr><td colSpan={4} className="text-center text-muted-foreground py-6">No temp invoices pending payment.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="card-flat overflow-hidden">
            <div className="p-4 border-b border-border">
              <div className="eyebrow">Details</div>
              <div className="font-heading text-lg font-semibold">Every Unpaid Temp Invoice</div>
            </div>
            <table className="data-table" data-testid="temps-details-table">
              <thead><tr><th>Temp #</th><th>Date</th><th>Customer</th><th className="num">Total</th><th className="num">Received</th><th className="num">Balance</th><th className="num">Age</th><th>Status</th><th>Supporting Company</th></tr></thead>
              <tbody>
                {temps.details.map((r) => (
                  <tr key={r.id}>
                    <td className="font-mono font-semibold">{r.number}</td>
                    <td className="font-mono">{fmtDate(r.date)}</td>
                    <td>{r.customer_name}</td>
                    <td className="num font-mono">{inr(r.grand_total)}</td>
                    <td className="num font-mono" style={{ color: "hsl(var(--success))" }}>{inr(r.received_amount)}</td>
                    <td className="num font-mono" style={{ color: "hsl(var(--warning))" }}>{inr(r.balance_amount)}</td>
                    <td className="num font-mono" style={r.age_days >= 10 ? { color: r.age_days >= 30 ? "hsl(var(--destructive))" : "hsl(var(--warning))" } : {}}>{r.age_days}d</td>
                    <td><span className={`pill ${r.status === "partial" ? "pill-info" : "pill-warning"}`}>{r.status}</span></td>
                    <td className="text-[11px]" title={(r.supporting_tenders || []).join("\n")}>
                      {(r.supporting_tenders || []).length ? (r.supporting_tenders || []).join(", ") : "—"}
                    </td>
                  </tr>
                ))}
                {temps.details.length === 0 && <tr><td colSpan={9} className="text-center text-muted-foreground py-6">Nothing pending. All temp invoices are paid or converted.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
export default function Reports() {
  const { activeCompanyId, activeStoreId } = useAuth();
  const [period, setPeriod] = useState("month");
  const [start, setStart] = useState(startOfMonth());
  const [end, setEnd] = useState(today());
  const [refreshKey, setRefreshKey] = useState(0);
  const onRefresh = () => setRefreshKey((k) => k + 1);

  // reset refresh when active company changes
  useEffect(() => { setRefreshKey((k) => k + 1); }, [activeCompanyId, activeStoreId]);

  return (
    <div className="page-shell space-y-6" id="report-print-root">
      <div>
        <div className="eyebrow">Business Intelligence</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Reports</h1>
        <p className="text-sm text-muted-foreground mt-1">Sales · Profit · Expenses · Pending Payments</p>
      </div>

      <div className="no-print">
        <FilterBar
          period={period} setPeriod={setPeriod}
          start={start} setStart={setStart}
          end={end} setEnd={setEnd}
          onRefresh={onRefresh}
        />
      </div>

      <Tabs defaultValue="sales">
        <TabsList>
          <TabsTrigger value="sales" data-testid="reports-tab-sales"><FileText size={13} className="mr-1.5" /> Sales Summary</TabsTrigger>
          <TabsTrigger value="profit" data-testid="reports-tab-profit"><TrendUp size={13} className="mr-1.5" /> Profit Summary</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="reports-tab-expenses"><Wallet size={13} className="mr-1.5" /> Expenses Summary</TabsTrigger>
          <TabsTrigger value="pending" data-testid="reports-tab-pending"><Warning size={13} className="mr-1.5" /> Pending Payments</TabsTrigger>
          <TabsTrigger value="customer-statement" data-testid="reports-tab-customer-statement"><FileText size={13} className="mr-1.5" /> Customer Statement</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="reports-tab-suppliers"><Wallet size={13} className="mr-1.5" /> Suppliers Details</TabsTrigger>
        </TabsList>
        <TabsContent value="sales" className="mt-4">
          <SalesTab period={period} start={start} end={end} refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="profit" className="mt-4">
          <ProfitTab period={period} start={start} end={end} refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="expenses" className="mt-4">
          <ExpensesTab period={period} start={start} end={end} refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="pending" className="mt-4">
          <PendingPaymentsTab refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="customer-statement" className="mt-4">
          <CustomerStatementTab period={period} start={start} end={end} refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="suppliers" className="mt-4">
          <SuppliersTab period={period} start={start} end={end} refreshKey={refreshKey} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
